package dev.lingvo.site.components.editor

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderLeft
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.KeyRow
import dev.lingvo.shared.QaSeverity
import dev.lingvo.shared.TranslationState
import dev.lingvo.site.ds.IconSize
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LSkeleton
import dev.lingvo.site.ds.LSkeletonShape
import dev.lingvo.site.ds.LVisuallyHiddenStyle
import dev.lingvo.site.ds.LWarningIcon
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.iconSize
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.trf
import kotlin.math.ceil
import kotlin.math.floor
import org.jetbrains.compose.web.attributes.ButtonType
import org.jetbrains.compose.web.attributes.type
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

internal const val KEY_SCROLLER_ID = "lingvo-editor-key-scroller"

@Composable
internal fun VirtualizedRows(
    model: KeyListPaneModel,
    virtualScrollTopPx: Double,
    viewportHeight: Double,
    onSelectKey: (String) -> Unit,
    onRetryPaging: () -> Unit,
) {
    val firstVisible = floor(virtualScrollTopPx / EDITOR_ROW_HEIGHT_PX).toInt().coerceAtLeast(0)
    val rowCount = model.rows.size
    val first = (firstVisible - EDITOR_LIST_OVERSCAN).coerceIn(0, rowCount)
    val visibleCapacity = ceil(viewportHeight / EDITOR_ROW_HEIGHT_PX).toInt() + EDITOR_LIST_OVERSCAN * 2
    val lastExclusive = (first + visibleCapacity).coerceIn(first, rowCount)

    Div(Modifier.attr("data-key-list-items", "true").toAttrs()) {
        Div(Modifier.height((first * EDITOR_ROW_HEIGHT_PX).px).toAttrs { attr("aria-hidden", "true") })
        model.rows.subList(first, lastExclusive).forEach { row ->
            KeyListRow(
                row = row,
                locale = model.locale,
                activePluralCategory = model.activePluralCategory,
                selected = row.key.id == model.selectedKeyId,
                severity = model.severityByKey[row.key.id],
                showTargetPreview = model.showTargetPreview,
                hasConflict = row.key.id in model.conflictKeyIds,
                onClick = { onSelectKey(row.key.id) },
            )
        }
        val rowsBelow = model.rows.size - lastExclusive
        Div(Modifier.height((rowsBelow * EDITOR_ROW_HEIGHT_PX).px).toAttrs { attr("aria-hidden", "true") })
        when {
            model.loadingMore -> KeyListLoadingMore()
            model.pagingError != null -> KeyListPagingError(
                message = model.pagingError,
                onRetry = onRetryPaging,
            )
        }
    }
}

@Composable
private fun KeyListRow(
    row: KeyRow,
    locale: String,
    activePluralCategory: dev.lingvo.shared.PluralCategory?,
    selected: Boolean,
    severity: QaSeverity?,
    showTargetPreview: Boolean,
    hasConflict: Boolean,
    onClick: () -> Unit,
) {
    val palette = ColorMode.current.toSitePalette()
    val state = row.target?.state ?: TranslationState.EMPTY
    val presentation = editorQueueRowPresentation(
        row = row,
        locale = locale,
        activePluralCategory = activePluralCategory,
        severity = severity,
        hasConflict = hasConflict,
    )
    // The status gutter is a progression — nothing, grey, green — independent of selection.
    //
    // It used to be muted / info / success, which put a slate-blue TRANSLATED rail directly above
    // an indigo selected rail in a 200-row list. Measured in OKLab through the Machado matrices,
    // those two are ΔE 11.2 apart in Night for normal vision and 9.8 for a deuteranope — two blues
    // nobody reliably separates — and 5.2 apart in Day for a tritanope. Against textMuted the same
    // pairs are 21.0 and 27.9.
    //
    // EMPTY draws no rail at all. Not a light grey one: an untranslated string has no state worth
    // marking, and giving every row a rail is how the rail stops meaning anything.
    //
    // An unresolved conflict overrides whatever the rail would otherwise say. The optimistic
    // advance means a translator can be several keys past the one a 409 landed on, with no other
    // reason to look back — the rail is the thing seen while scanning past, not while reading one
    // row, so it has to outrank TRANSLATED/APPROVED rather than sit beside them.
    val stateColor = if (hasConflict) {
        palette.danger
    } else {
        when (state) {
            TranslationState.EMPTY -> Colors.Transparent
            TranslationState.TRANSLATED -> palette.textMuted
            TranslationState.APPROVED -> palette.success
        }
    }
    // A soft fill and stronger source/key text mark selection without a perimeter ring.
    // Keep the state rail independent and the fixed virtual row geometry unchanged; keyboard
    // focus retains its separate focus-visible outline in KeyRowStyle.
    val selectedModifier = Modifier
        .then(
            if (selected) Modifier.backgroundColor(palette.accentSubtle) else Modifier,
        )
        // Match the transparent width reserved by KeyRowStyle so state changes never move text.
        .borderLeft(STATE_RAIL_PX.px, LineStyle.Solid, stateColor)
    val accessibleLabel = buildString {
        append(presentation.source)
        append(", ")
        append(presentation.key)
        append(", ")
        append(presentation.state)
        if (presentation.machine) append(", Machine")
        presentation.qaText?.let { append(", QA $it") }
        presentation.conflictText?.let { append(", $it") }
    }

    Button(
        KeyRowStyle.toModifier()
            .then(selectedModifier)
            .toAttrs {
                id("$KEY_ROW_ID_PREFIX${row.key.id}")
                type(ButtonType.Button)
                attr("data-editor-queue-row", "true")
                attr("aria-current", selected.toString())
                attr("aria-label", accessibleLabel)
                onClick { onClick() }
            },
    ) {
        Column(Modifier.fillMaxWidth().gap(Space.XXS)) {
            Span(
                KeySourceStyle.toModifier()
                    .then(if (selected) Modifier.fontWeight(700) else Modifier)
                    .toAttrs {
                        attr("title", row.sourceText())
                        // Han is unified in Unicode, so without `lang` a reviewer whose own locale
                        // isn't the source's reads someone else's glyph forms. This is the pane scanned
                        // for two hours; it is the one that most needed this and had neither attribute.
                        attr("lang", row.source.locale)
                        attr("dir", "auto")
                    },
            ) {
                Text(presentation.source)
            }
            Row(
                Modifier.fillMaxWidth().gap(Space.XS),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Span(
                    KeyNameStyle.toModifier()
                        .then(if (selected) Modifier.fontWeight(700) else Modifier)
                        .styleModifier { property("flex", "1 1 auto") }
                        .toAttrs {
                            attr("title", presentation.key)
                        },
                ) {
                    Text(presentation.key)
                }
                // The state used to repeat here as a word — "Untranslated"/"Translated"/"Approved"
                // — on every row. It is already the rail's colour (above), the empty-rail
                // convention, and the accessible label (below); a fourth copy spent width the mono
                // key name and the target preview need, and under a state filter it was the same
                // word on every row in the pane.
                if (showTargetPreview && presentation.targetPreview != null) {
                    Span(KeyTargetPreviewStyle.toModifier().toAttrs {
                        attr("data-editor-target-preview", "true")
                        attr("title", presentation.targetPreview)
                        attr("lang", locale)
                        attr("dir", "auto")
                    }) {
                        Text(presentation.targetPreview)
                    }
                }
                if (presentation.machine) {
                    Span(KeyMachineLabelStyle.toModifier().toAttrs()) { Text(t("editor.editor_queue_rows.ai", "Machine")) }
                }
                // **A dot, not a sentence.** `docs/DESIGN.md`'s second QA layer asks for "a small
                // severity dot on affected rows so problems are findable without opening keys" —
                // findable, which is a scanning job, not a reading one. This spent about 55px per
                // affected row on a warning triangle plus the word "Error", inside a 304px pane,
                // and what it took that space from was the mono key name the developer navigates
                // by: five of thirty-one rows were truncating.
                //
                // The word is not lost, it moves to where words belong. `aria-label` carries the
                // full severity for a screen reader and `title` shows it on hover, so nothing is
                // encoded in colour alone — the dot's position and its tooltip both survive a
                // reader who cannot tell danger from warning by hue.
                presentation.qaText?.let { qaText ->
                    Span(
                        KeyQaDotStyle.toModifier()
                            .backgroundColor(
                                if (severity == QaSeverity.ERROR) {
                                    ColorMode.current.toSitePalette().danger
                                } else {
                                    ColorMode.current.toSitePalette().warning
                                },
                            )
                            .attr("data-editor-queue-qa", "true")
                            .attr("role", "img")
                            .attr("title", trf(
                                "editor.editor_queue_rows.qa_summary",
                                "QA {summary}",
                                "summary" to qaText,
                            ))
                            .attr("aria-label", trf(
                                "editor.editor_queue_rows.qa_summary",
                                "QA {summary}",
                                "summary" to qaText,
                            ))
                            .toAttrs(),
                    ) {}
                }
                // Same dot, same danger tone the QA dot already uses for ERROR — not a new
                // colour, and never colour alone: `title` shows it on hover and `aria-label`
                // carries it to a screen reader, same as the QA dot beside it. A key with an
                // unresolved conflict must be findable by scanning the queue, because the
                // optimistic advance (see TranslationEditorState.saveKey) means a translator can
                // easily have moved several keys past the one a 409 landed on.
                // **A glyph, not a third dot.** Two identical dots in the same 12px column, one
                // meaning "QA has an objection" and the other "two people saved this", were
                // distinguishable only by hovering. The QA dot stays a dot because it grades a
                // severity; a conflict is an event, and a triangle is the mark the rest of the
                // product already uses for one. Colour is still never the only signal: `title` on
                // hover and `aria-label` for a reader, exactly as before.
                presentation.conflictText?.let { conflictText ->
                    Span(
                        Modifier
                            .color(palette.danger)
                            .iconSize(IconSize.XS)
                            .attr("data-editor-queue-conflict", "true")
                            .attr("role", "img")
                            .attr("title", conflictText)
                            .attr("aria-label", conflictText)
                            .toAttrs(),
                    ) {
                        LWarningIcon()
                    }
                }
            }
        }
    }
}

@Composable
internal fun KeyListSkeleton() {
    Column(Modifier.padding(Space.SM).gap(Space.XXS)) {
        // One region for the set. Each `LSkeleton` is its own `role="status"`, so eight rows of two
        // bars announced "Loading keys" sixteen times before the queue had said anything.
        Span(LVisuallyHiddenStyle.toModifier().toAttrs { attr("role", "status") }) {
            Text(t("editor.editor_queue_rows.loading_keys", "Loading keys"))
        }
        repeat(8) {
            Column(
                Modifier
                    .fillMaxWidth()
                    .height(EDITOR_ROW_HEIGHT_PX.px)
                    .gap(Space.XXS),
            ) {
                LSkeleton(width = 86.percent, height = 16.px, shape = LSkeletonShape.TEXT, label = null)
                LSkeleton(width = 54.percent, height = 12.px, shape = LSkeletonShape.TEXT, label = null)
            }
        }
    }
}

@Composable
internal fun KeyListLoadingMore() {
    Span(LVisuallyHiddenStyle.toModifier().toAttrs { attr("role", "status") }) {
        Text(t("editor.editor_queue_rows.loading_more_keys", "Loading more keys"))
    }
    Column(
        Modifier
            .padding(Space.SM)
            .gap(Space.XXS)
            .attr("aria-label", t("editor.editor_queue_rows.loading_more_keys", "Loading more keys")),
    ) {
        repeat(2) {
            LSkeleton(height = EDITOR_ROW_HEIGHT_PX.px, label = null)
        }
    }
}

@Composable
private fun KeyListPagingError(
    message: String,
    onRetry: () -> Unit,
) {
    Column(
        Modifier
            .fillMaxWidth()
            .padding(Space.SM)
            .gap(Space.XS)
            .color(ColorMode.current.toSitePalette().danger)
            .attr("data-editor-queue-paging-error", "true")
            .attr("role", "alert"),
    ) {
        Span { Text(message) }
        LButton(
            label = t("editor.editor_queue_rows.retry", "Retry"),
            onClick = onRetry,
            variant = LButtonVariant.SECONDARY,
        )
    }
}
